/**
 * Shared multi-file picker (Tax Mail-Away response contract):
 * PDF/DOC/DOCX/JPG/PNG · ≤10 MB per file · up to `maxFiles` · ≤ `maxTotalMb` total.
 */
import React from 'react';
import { Upload, FileText, X, AlertCircle } from 'lucide-react';
import { useDropZone } from '../../../hooks/useDropZone';

export const MAIL_AWAY_FILE_TYPES = [
  'application/pdf', 'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg', 'image/png',
];
export const MAIL_AWAY_FILE_EXTS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'];

export const useMailAwayFiles = ({ maxFiles = 3, maxTotalMb = 30, maxSizeMb = 10, onAccepted } = {}) => useDropZone({
  allowedTypes: MAIL_AWAY_FILE_TYPES,
  allowedExtensions: MAIL_AWAY_FILE_EXTS,
  maxSize: maxSizeMb * 1024 * 1024,
  multiple: true,
  appendMode: true,
  maxFiles,
  maxTotalSize: maxTotalMb * 1024 * 1024,
  onFilesAccepted: onAccepted,
});

const ACCENTS = {
  green: { drag: 'border-green-500 bg-green-50', card: 'bg-green-50 border-green-200', icon: 'text-green-600' },
  blue: { drag: 'border-blue-500 bg-blue-50', card: 'bg-blue-50 border-blue-200', icon: 'text-blue-600' },
};

export const MailAwayFilePicker = ({ zone, error, onClearError, maxFiles = 3, maxTotalMb = 30, maxSizeMb = 10, helperText, compact = false, accent = 'green', testIdPrefix = 'mailaway' }) => {
  const ac = ACCENTS[accent] || ACCENTS.green;
  const files = Array.isArray(zone.file) ? zone.file : [];
  const err = error || zone.error;
  const total = files.reduce((s, f) => s + (f.size || 0), 0) / 1024 / 1024;
  return (
    <div>
      {!compact && <p className="text-xs text-gray-500 mb-2">{helperText || `PDF, DOC, DOCX, JPG, PNG • Maximum ${maxSizeMb} MB per file • Up to ${maxFiles} files • ${maxTotalMb} MB total`}</p>}
      {files.length < maxFiles && (
        <div {...zone.dropzoneProps}
          className={`border-2 border-dashed rounded-xl ${compact ? 'p-3' : 'p-4'} text-center transition-[border-color,background-color,transform] duration-200 cursor-pointer ${
            err ? 'border-red-400 bg-red-50' : zone.isDragging ? `${ac.drag} scale-[1.01]` : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/40'}`}
          data-testid={`${testIdPrefix}-dropzone`}>
          <Upload size={compact ? 18 : 22} className={`mx-auto mb-1 ${err ? 'text-red-400' : 'text-blue-500'}`} />
          <p className="text-sm text-gray-600">
            {files.length === 0
              ? <>Drag &amp; drop files here or <span className="text-blue-600 hover:text-blue-800 font-medium">browse</span></>
              : <>Add more files ({files.length}/{maxFiles} selected)</>}
          </p>
          {compact && <p className="text-[11px] text-gray-400 mt-1">{helperText || `PDF, DOC, DOCX, JPG, PNG • Max ${maxSizeMb} MB per file • Up to ${maxFiles} files • ${maxTotalMb} MB total`}</p>}
          <input {...zone.inputProps} data-testid={`${testIdPrefix}-file-input`} />
        </div>
      )}
      {files.length > 0 && (
        <div className={`space-y-2 ${files.length < maxFiles ? 'mt-3' : ''}`} data-testid={`${testIdPrefix}-selected-files`}>
          {files.map((f, idx) => (
            <div key={`${f.name}-${idx}-${f.size}`}
              className={`flex items-center gap-3 p-2.5 border-2 rounded-lg ${ac.card}`}
              data-testid={`${testIdPrefix}-selected-file-${idx}`}>
              <FileText size={18} className={`${ac.icon} flex-shrink-0`} />
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-gray-900 truncate">{f.name}</p>
                <p className="text-xs text-gray-500">{(f.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); zone.removeAt(idx); onClearError?.(); }}
                className="p-1 hover:bg-gray-200 rounded" title="Remove file" data-testid={`${testIdPrefix}-remove-file-${idx}`}>
                <X size={14} />
              </button>
            </div>
          ))}
          <p className="text-xs text-gray-500 text-right" data-testid={`${testIdPrefix}-total-size`}>
            Total: {total.toFixed(2)} MB / {maxTotalMb.toFixed(2)} MB
          </p>
        </div>
      )}
      {err && (
        <p className="mt-2 text-sm text-red-600 flex items-center gap-1" data-testid={`${testIdPrefix}-file-error`}>
          <AlertCircle size={14} />{err}
        </p>
      )}
    </div>
  );
};
